import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { getBookingSubmissions } from "@/lib/api";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Calendar, Download, User, Mail, Phone, Building2, Loader2,
  Filter, Clock, Briefcase, MessageSquare
} from "lucide-react";

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, calendly_booking, consultation_booking
  const [exporting, setExporting] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => { loadBookings(); }, [filter]);

  const loadBookings = async () => {
    setLoading(true);
    try {
      const res = await getBookingSubmissions(filter);
      setBookings(res.data || []);
    } catch { toast.error("Failed to load bookings"); }
    setLoading(false);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("auth_token");
      const src = filter !== "all" ? `&source=${filter}` : "";
      const response = await fetch(`${API_URL}/api/leads/bookings/export?${src}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("Export failed");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "booking_submissions.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Excel exported successfully");
    } catch { toast.error("Export failed"); }
    setExporting(false);
  };

  const demoCount = bookings.filter((b) => b.source === "calendly_booking" || b.source === "odoo_booking").length;
  const consultCount = bookings.filter((b) => b.source === "consultation_booking").length;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3" data-testid="bookings-title">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              Booking Submissions
            </h1>
            <p className="text-sm text-slate-500 mt-1">View and export demo & consultation requests</p>
          </div>
          <Button onClick={handleExport} disabled={exporting || bookings.length === 0} variant="outline" className="gap-2" data-testid="export-bookings-btn">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Export Excel
          </Button>
        </div>

        {/* Stats + Filter */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: "All Bookings", value: bookings.length, key: "all", color: "bg-slate-100 text-slate-700" },
            { label: "Demo Requests", value: demoCount, key: "calendly_booking", color: "bg-blue-100 text-blue-700" },
            { label: "Consultations", value: consultCount, key: "consultation_booking", color: "bg-emerald-100 text-emerald-700" },
          ].map((s) => (
            <button key={s.key} onClick={() => setFilter(s.key)}
              className={`p-4 rounded-xl border-2 text-left transition-colors ${filter === s.key ? "border-[#0066CC] bg-blue-50" : "border-transparent bg-white"}`}
              data-testid={`filter-${s.key}`}>
              <p className="text-2xl font-bold text-slate-900">{s.value}</p>
              <p className="text-sm text-slate-500">{s.label}</p>
            </button>
          ))}
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
        ) : bookings.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
            <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No booking submissions found</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Contact</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Company</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Type</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Interest</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Message</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`booking-row-${b.id}`}>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-slate-900 flex items-center gap-1.5">
                            <User className="w-3.5 h-3.5 text-slate-400" />
                            {b.name}
                          </p>
                          <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                            <Mail className="w-3 h-3" />{b.email}
                          </p>
                          {b.phone && (
                            <p className="text-xs text-slate-400 flex items-center gap-1">
                              <Phone className="w-3 h-3" />{b.phone}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-slate-700">{b.company || "—"}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${
                          (b.source === "calendly_booking" || b.source === "odoo_booking") ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"
                        }`}>
                          {(b.source === "calendly_booking" || b.source === "odoo_booking") ? <><Briefcase className="w-3 h-3" /> Demo</> : <><MessageSquare className="w-3 h-3" /> Consultation</>}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-slate-600 text-xs">{b.vertical_interest || b.problem_interest || "—"}</span>
                      </td>
                      <td className="px-4 py-3 max-w-[200px]">
                        <p className="text-xs text-slate-500 truncate">{b.message || "—"}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {b.created_at ? new Date(b.created_at).toLocaleDateString() : "—"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
